<template>
  <header class="admin-header">
    <!-- Tombol toggle sidebar untuk layar sempit maupun layar lebar -->
    <button
      type="button"
      class="admin-header__menu"
      :aria-label="sidebarTerbuka ? 'Tutup sidebar' : 'Buka sidebar'"
      :title="sidebarTerbuka ? 'Tutup sidebar' : 'Buka sidebar'"
      @click="toggleMenu"
    >
      <i class="bi bi-list" aria-hidden="true"></i>
    </button>

    <span class="admin-header__judul">{{ judulHalaman }}</span>

    <div class="admin-header__kanan">
      <!-- Ganti tema: panitia sering bekerja di gelanggang yang gelap. -->
      <button
        type="button"
        class="admin-header__ikon"
        :aria-label="gelap ? 'Ganti ke mode terang' : 'Ganti ke mode gelap'"
        @click="gantiTema"
      >
        <i class="bi" :class="gelap ? 'bi-sun' : 'bi-moon-stars'" aria-hidden="true"></i>
      </button>

      <div class="admin-header__akun" ref="wadahAkun">
        <button
          type="button"
          class="admin-header__pemicu"
          :aria-expanded="String(menuAkunTerbuka)"
          @click="menuAkunTerbuka = !menuAkunTerbuka"
        >
          <span class="admin-header__inisial" aria-hidden="true">{{ inisial }}</span>
          <span class="admin-header__nama">{{ namaPengguna }}</span>
          <i class="bi bi-chevron-down admin-header__panah" aria-hidden="true"></i>
        </button>

        <div v-if="menuAkunTerbuka" class="admin-header__panel" role="menu">
          <span class="admin-header__panel-nama">{{ namaPengguna }}</span>
          <span class="admin-header__panel-surel">{{ surelPengguna }}</span>
          <hr class="admin-header__garis" />
          <router-link to="/" class="admin-header__aksi" role="menuitem">
            <i class="bi bi-box-arrow-up-right" aria-hidden="true"></i>
            Lihat situs publik
          </router-link>
          <button
            type="button"
            class="admin-header__aksi admin-header__aksi--keluar"
            role="menuitem"
            :disabled="keluarBerjalan"
            @click="keluar"
          >
            <i class="bi bi-box-arrow-right" aria-hidden="true"></i>
            {{ keluarBerjalan ? 'Keluar...' : 'Keluar' }}
          </button>
        </div>
      </div>

      <!-- Tombol Keluar Langsung di Header (Selalu Terlihat Jelas) -->
      <button
        type="button"
        class="admin-header__tombol-keluar"
        :disabled="keluarBerjalan"
        :title="keluarBerjalan ? 'Sedang keluar...' : 'Keluar / Logout'"
        :aria-label="'Keluar'"
        @click="keluar"
      >
        <i class="bi bi-box-arrow-right" aria-hidden="true"></i>
        <span class="admin-header__label-keluar">{{ keluarBerjalan ? 'Keluar...' : 'Keluar' }}</span>
      </button>
    </div>
  </header>
</template>

<script>
import { grupPengguna } from '@/utils/peran';
import { useToast } from 'vue-toastification';
import http from '@/services/http';
import { useTheme } from '@/composables/useTheme';

export default {
  name: 'AdminHeader',

  props: {
    sidebarTerbuka: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['toggle-sidebar'],

  setup() {
    const { isDark, toggleTheme } = useTheme();
    return { toast: useToast(), gelap: isDark, gantiTema: toggleTheme };
  },

  data() {
    return {
      namaPengguna: 'Pengguna',
      surelPengguna: '',
      menuAkunTerbuka: false,
      keluarBerjalan: false,
    };
  },

  computed: {
    inisial() {
      return (this.namaPengguna || '?')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((kata) => kata[0])
        .join('')
        .toUpperCase();
    },
    grup() {
      return grupPengguna();
    },
    isPanitia() {
      try {
        const data = JSON.parse(localStorage.getItem('user') || '{}');
        const group = data.group?.name || '';
        return ['Owner', 'Event PIC', 'Admin'].includes(group);
      } catch (e) {
        return true;
      }
    },
    judulHalaman() {
      const path = this.$route.path;
      if (path === '/admin' || path === '/admin/dashboard') return 'Dashboard';
      if (path.startsWith('/admin/team-members')) return 'Data Atlet';
      if (path.startsWith('/admin/payment') || path.startsWith('/admin/buat-tagihan')) return 'Tagihan';
      if (path.startsWith('/admin/ketersediaan-kelas')) return 'Ketersediaan Kelas';
      if (path.startsWith('/admin/contingent')) return 'Data Kontingen';
      if (path.startsWith('/admin/keuangan')) return 'Keuangan';
      // Awalan yang lebih panjang harus diperiksa lebih dulu: '/admin/tournament'
      // juga cocok dengan '/admin/tournament-age-category' dan sebelumnya
      // membuat judul halaman-halaman itu selalu tertulis "Turnamen".
      if (path.startsWith('/admin/tournament-age-category')) return 'Kategori Usia';
      if (path.startsWith('/admin/tournament-category')) return 'Kategori Kejuaraan';
      if (path.startsWith('/admin/tournament-schedule')) return 'Jadwal Pertandingan';
      if (path.startsWith('/admin/tournament-arena')) return 'Gelanggang';
      if (path.startsWith('/admin/tournament-activity')) return 'Kegiatan';
      if (path.startsWith('/admin/tournament-contact-person')) return 'Narahubung';
      if (/^\/admin\/tournament\/\d+\/pengguna/.test(path)) return 'Pengguna Kejuaraan';
      if (path.startsWith('/admin/turnamen-saya') || path.startsWith('/admin/tournament')) return 'Turnamen';
      if (path.startsWith('/admin/classes')) return 'Kelas Tanding';
      if (path.startsWith('/admin/jenis-tarif')) return 'Pengaturan Tarif';
      if (path.startsWith('/admin/id-card')) return 'ID Card';
      if (path.startsWith('/admin/perkiraan-medali')) return 'Perkiraan Medali';
      if (path.startsWith('/admin/match-chart')) return 'Bagan Tanding';
      if (path.startsWith('/admin/seni-match')) return 'Bagan Seni';
      if (path.startsWith('/admin/drawing')) return 'Undian & Pool';
      if (path.startsWith('/admin/navigation')) return 'Menu Navigasi';
      if (path.startsWith('/admin/sliders')) return 'Slider';
      if (this.grup === 'Bendahara') return 'Panel Bendahara';
      return this.isPanitia ? 'Panel Panitia' : 'Panel Official';
    },
  },

  created() {
    this.periksaSesi();
  },

  mounted() {
    document.addEventListener('click', this.klikDiLuar);
    document.addEventListener('keydown', this.tekanEscape);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.klikDiLuar);
    document.removeEventListener('keydown', this.tekanEscape);
  },

  methods: {
    periksaSesi() {
      const token = localStorage.getItem('authToken');

      if (!token) {
        /*
        | Sebelumnya ini memanggil toast.success dengan pesan "Token not
        | found" — nada hijau untuk kabar buruk, dan dalam bahasa Inggris di
        | antarmuka berbahasa Indonesia. Sesi habis bukan keberhasilan.
        */
        this.toast.info('Sesi Anda sudah berakhir. Silakan masuk lagi.');
        this.$router.push('/admin/login');
        return;
      }

      try {
        const data = JSON.parse(localStorage.getItem('user') || '{}');
        this.namaPengguna = data.name || 'Pengguna';
        this.surelPengguna = data.email || '';
      } catch (e) {
        this.namaPengguna = 'Pengguna';
      }
    },


    klikDiLuar(peristiwa) {
      if (!this.menuAkunTerbuka) return;
      if (!this.$refs.wadahAkun?.contains(peristiwa.target)) {
        this.menuAkunTerbuka = false;
      }
    },

    tekanEscape(peristiwa) {
      if (peristiwa.key === 'Escape') this.menuAkunTerbuka = false;
    },

    toggleMenu() {
      this.$emit('toggle-sidebar');
    },

    async keluar() {
      this.keluarBerjalan = true;
      try {
        await http.post('/logout');
      } catch (e) {
        // Kegagalan di sisi peladen tidak boleh menahan pengguna di dalam
        // panel: token lokal tetap dibuang supaya "keluar" benar-benar keluar.
      } finally {
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
        this.keluarBerjalan = false;
        this.toast.success('Anda sudah keluar.');
        this.$router.push('/admin/login');
      }
    },
  },
};
</script>

<style scoped>
.admin-header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: auto;
  height: 100%;
}

.admin-header__menu {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  min-width: 42px;
  min-height: 42px;
  padding: 0;
  font-size: 1.25rem;
  color: var(--text-secondary);
  background: var(--bg-sunken);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  cursor: pointer;
  flex-shrink: 0;
  transition: all var(--transition-fast);
}

.admin-header__menu:hover {
  background: var(--bg-hover);
  color: var(--accent);
  border-color: var(--border-strong);
}

.admin-header__judul {
  font-family: var(--font-sans);
  font-size: 0.88rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0;
  background: transparent;
  border: none;
}

.admin-header__kanan {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-left: auto;
}

.admin-header__ikon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  min-width: 42px;
  min-height: 42px;
  padding: 0;
  font-size: 1.05rem;
  color: var(--text-secondary);
  background: var(--bg-sunken);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  cursor: pointer;
  flex-shrink: 0;
  transition: all var(--transition-fast);
}

.admin-header__ikon:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
  border-color: var(--border-strong);
}

/* Memastikan ikon di dalam tombol benar-benar simetris ke tengah */
.admin-header__menu :deep(i.bi),
.admin-header__ikon :deep(i.bi),
.admin-header__menu i.bi,
.admin-header__ikon i.bi {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.admin-header__menu :deep(i.bi::before),
.admin-header__ikon :deep(i.bi::before),
.admin-header__menu i.bi::before,
.admin-header__ikon i.bi::before {
  vertical-align: 0 !important;
  line-height: 1 !important;
  margin: 0 !important;
}

/* ── Akun ── */
.admin-header__akun { position: relative; }

.admin-header__pemicu {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 42px;
  padding: 3px 12px 3px 5px;
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-primary);
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-full);
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  transition: all var(--transition-fast);
}

.admin-header__pemicu:hover {
  background: var(--bg-hover);
  border-color: var(--border-strong);
}

.admin-header__inisial {
  display: grid;
  place-items: center;
  width: 28px; height: 28px;
  font-size: var(--text-xs);
  font-weight: 700;
  color: #fff;
  background: #F05008;
  border-radius: 50%;
  flex-shrink: 0;
}

.admin-header__panah { font-size: 0.65rem; opacity: 0.6; }

.admin-header__panel {
  position: absolute;
  top: calc(100% + var(--space-2));
  right: 0;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  min-width: 220px;
  padding: var(--space-3);
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}

.admin-header__panel-nama {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-primary);
}

.admin-header__panel-surel {
  font-size: var(--text-xs);
  color: var(--text-muted);
  overflow-wrap: anywhere;
}

.admin-header__garis {
  margin: var(--space-3) 0;
  border: none;
  border-top: 1px solid var(--border-subtle);
}

.admin-header__aksi {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  min-height: 40px;
  padding: var(--space-2) var(--space-3);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: 600;
  text-align: left;
  text-decoration: none;
  color: var(--text-secondary);
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
}

.admin-header__aksi:hover { background: var(--bg-hover); color: var(--text-primary); }
.admin-header__aksi--keluar { color: var(--danger-600); }
.admin-header__aksi--keluar:hover { background: rgb(220 38 38 / 0.10); color: var(--danger-600); }
.admin-header__aksi:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Tombol Keluar Langsung di Bar Atas ── */
.admin-header__tombol-keluar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  height: 42px;
  min-height: 42px;
  padding: 0 0.95rem;
  font-family: var(--font-sans);
  font-size: var(--text-xs, 0.78rem);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.25);
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
  flex-shrink: 0;
  transition: all var(--transition-fast);
}

.admin-header__tombol-keluar:hover {
  background: rgba(239, 68, 68, 0.18);
  border-color: rgba(239, 68, 68, 0.45);
  color: #dc2626;
  transform: translateY(-1px);
}

.admin-header__tombol-keluar:active {
  transform: translateY(0);
}

.admin-header__tombol-keluar:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.admin-header__tombol-keluar i.bi {
  font-size: 1.1rem;
  line-height: 1;
}

/* ── Layar lebar: tombol menu tetap ditampilkan untuk buka/tutup bilah sisi ── */
@media (min-width: 992px) {
  /* Tombol menu tetap tampil */
}

/* Nama disembunyikan di layar sempit; lingkaran inisial sudah cukup untuk
   menandakan siapa yang sedang masuk, dan judul tidak jadi terhimpit. */
@media (max-width: 600px) {
  .admin-header__nama { display: none; }
  .admin-header__pemicu { padding: 4px 8px 4px 4px; }
  .admin-header__judul {
    display: block;
    font-size: 0.78rem;
    max-width: 130px;
    letter-spacing: 0.02em;
  }
  .admin-header__label-keluar {
    display: none;
  }
  .admin-header__tombol-keluar {
    width: 42px;
    height: 42px;
    min-width: 42px;
    padding: 0;
  }
}
</style>
